import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { uploadModeratedMedia, mergeModerationTokens } from '../utils/moderatedUpload';
import type { ModerationTokenMap } from '../utils/moderatedUpload';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../components/TopNavigation';
import VerifiedBadge from '../components/VerifiedBadge';
import ImageWithFallback from '../components/ImageWithFallback';
import { createPage, deletePage, listMyPages, listUsersByIds, searchUsers, updatePage, updatePageTeam } from '../utils/secureFunctions';

interface Page {
  id: string;
  name: string;
  username: string;
  type: 'business' | 'organization' | 'brand' | 'community';
  description: string;
  category: string;
  photoURL: string;
  coverPhotoURL: string;
  website?: string;
  email?: string;
  phone?: string;
  location?: string;
  ownerId: string;
  admins: string[];
  editors: string[];
  followers: string[];
  verified: boolean;
  createdAt: any;
}

const BusinessPages: React.FC = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    try { return window.innerWidth <= 1024; } catch { return false; }
  });
  useEffect(() => {
    const onResize = () => { try { setIsMobile(window.innerWidth <= 1024); } catch {} };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedPage, setSelectedPage] = useState<Page | null>(null);
  const [showManageModal, setShowManageModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [managingUsers, setManagingUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  
  // Form state
  const [pageName, setPageName] = useState('');
  const [pageUsername, setPageUsername] = useState('');
  const [pageType, setPageType] = useState<'business' | 'organization' | 'brand' | 'community'>('business');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    if (!auth.currentUser) return;
    
    setLoading(true);
    try {
      const res = await listMyPages();
      setPages(res.pages as Page[]);
    } catch (error) {
      console.error('Error loading pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePage = async () => {
    if (!auth.currentUser || !pageName || !pageUsername || !description) {
      alert('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      let photoURL = '';
      let coverPhotoURL = '';
      let moderationTokens: ModerationTokenMap | undefined;

      if (photoFile) {
        const upload = await uploadModeratedMedia({
          folder: 'pages',
          field: 'photoURL',
          file: photoFile,
          contentType: photoFile.type || 'image/jpeg',
          onProgress: (pct) => {
            const total = coverFile ? (pct * 0.5) : pct;
            setUploadProgress(total);
          },
        });
        photoURL = upload.objectKey;
        moderationTokens = mergeModerationTokens(moderationTokens, upload);
      }

      if (coverFile) {
        const upload = await uploadModeratedMedia({
          folder: 'pages',
          field: 'coverPhotoURL',
          file: coverFile,
          contentType: coverFile.type || 'image/jpeg',
          onProgress: (pct) => {
            const total = photoFile ? (50 + pct * 0.5) : pct;
            setUploadProgress(total);
          },
        });
        coverPhotoURL = upload.objectKey;
        moderationTokens = mergeModerationTokens(moderationTokens, upload);
      }

      // Create page document
      const pageData = {
        name: pageName,
        username: pageUsername.toLowerCase(),
        type: pageType,
        description,
        category,
        photoURL: photoURL || '',
        coverPhotoURL: coverPhotoURL || '',
        website,
        email,
        phone,
        location,
        ownerId: auth.currentUser.uid,
        admins: [auth.currentUser.uid],
        editors: [],
        followers: [],
        verified: false,
        moderationTokens: moderationTokens || null,
      };

      await createPage(pageData);
      
      alert('Page created successfully!');
      setShowCreateModal(false);
      resetForm();
      loadPages();
    } catch (error) {
      console.error('Error creating page:', error);
      alert('Failed to create page');
    } finally {
      setCreating(false);
      setUploadProgress(0);
    }
  };

  const resetForm = () => {
    setPageName('');
    setPageUsername('');
    setPageType('business');
    setDescription('');
    setCategory('');
    setWebsite('');
    setEmail('');
    setPhone('');
    setLocation('');
    setPhotoFile(null);
    setCoverFile(null);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <TopNavigation />
      </div>
    );
  }


  return (
    <div style={styles.container}>
  <TopNavigation />
      
      <div style={{ ...styles.main, ...(isMobile ? styles.mainMobile : {}) }}>
        <div style={styles.header}>
          <h1 style={styles.title}>Business Pages</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            style={styles.createButton}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
          >
            + Create Page
          </button>
        </div>

        {pages.length === 0 ? (
          <div style={styles.emptyState}>
            <h3>No pages yet</h3>
            <p>Create your first business or organization page to get started!</p>
            <button
              onClick={() => setShowCreateModal(true)}
              style={styles.emptyButton}
            >
              Create Your First Page
            </button>
          </div>
        ) : (
          <div style={{ ...styles.pagesGrid, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) }}>
            {pages.map((page) => (
              <div
                key={page.id}
                style={styles.pageCard}
                onClick={() => navigate(`/page/${page.id}`)}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
              >
                <div style={styles.cardCover}>
                  <ImageWithFallback src={page.coverPhotoURL} alt="" style={styles.coverImage} />
                </div>
                <div style={styles.cardContent}>
                  {(page.ownerId === auth.currentUser?.uid || page.admins.includes(auth.currentUser?.uid || '')) && (
                    <button 
                      style={{ position: 'absolute', top: 12, right: 12, padding: '6px 10px', fontSize: 12, borderRadius: 6, backgroundColor: '#FF9800', color: '#fff', border: 'none', cursor: 'pointer' }}
                      onClick={(e) => { e.stopPropagation(); setSelectedPage(page); setShowManageModal(true); }}
                    >
                      Manage
                    </button>
                  )}
                  <ImageWithFallback src={page.photoURL} alt="" style={styles.pageAvatar} />
                  <h3 style={styles.pageName}>{page.name}{page.verified && <span style={{ marginLeft: 4, verticalAlign: 'middle' }}><VerifiedBadge size={14} /></span>}</h3>
                  <p style={styles.pageUsername}>@{page.username}</p>
                  <p style={styles.pageType}>
                    {page.type.charAt(0).toUpperCase() + page.type.slice(1)}
                  </p>
                  <p style={styles.pageFollowers}>
                    {page.followers.length} followers
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Page Modal */}
      {showCreateModal && (
        <>
          <div style={styles.modalOverlay} onClick={() => setShowCreateModal(false)} />
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Create New Page</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                style={styles.closeButton}
              >
                ×
              </button>
            </div>

            <div style={styles.modalContent}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Page Name *</label>
                <input
                  type="text"
                  value={pageName}
                  onChange={(e) => setPageName(e.target.value)}
                  placeholder="My Business Name"
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Username * (unique)</label>
                <input
                  type="text"
                  value={pageUsername}
                  onChange={(e) => setPageUsername(e.target.value.replace(/[^a-z0-9_]/g, ''))}
                  placeholder="mybusiness"
                  style={styles.input}
                />
                <small style={styles.hint}>Only lowercase letters, numbers, and underscores</small>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Page Type *</label>
                <select
                  value={pageType}
                  onChange={(e) => setPageType(e.target.value as any)}
                  style={styles.select}
                >
                  <option value="business">Business</option>
                  <option value="organization">Organization</option>
                  <option value="brand">Brand</option>
                  <option value="community">Community</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Category</label>
                <input
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="e.g., Restaurant, Fashion, Tech"
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description *</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Tell people about your page..."
                  style={styles.textarea}
                  rows={4}
                />
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Website</label>
                  <input
                    type="url"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://example.com"
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="contact@example.com"
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Phone</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Location</label>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="City, Country"
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Page Photo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                  style={styles.fileInput}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Cover Photo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
                  style={styles.fileInput}
                />
              </div>

              {uploadProgress > 0 && (
                <div style={styles.progressBar}>
                  <div style={{...styles.progressFill, width: `${uploadProgress}%`}} />
                </div>
              )}

              <div style={styles.modalActions}>
                <button
                  onClick={handleCreatePage}
                  disabled={creating}
                  style={styles.submitButton}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
                >
                  {creating ? 'Creating...' : 'Create Page'}
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  style={styles.cancelButton}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Page Management Modal */}
      {showManageModal && selectedPage && (
        <>
          <div style={styles.modalOverlay} onClick={() => setShowManageModal(false)} />
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Manage Page</h2>
              <button
                onClick={() => setShowManageModal(false)}
                style={styles.closeButton}
              >
                ✕
              </button>
            </div>

            <div style={styles.modalContent}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <ImageWithFallback src={selectedPage.photoURL} alt="" style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', margin: '0 auto' }} />
                <h3 style={{ margin: '12px 0 4px', fontSize: '20px', color: '#072B2C' }}>{selectedPage.name}{selectedPage.verified && <span style={{ marginLeft: 6, verticalAlign: 'middle' }}><VerifiedBadge size={18} /></span>}</h3>
                <p style={{ margin: 0, color: '#5B6E6E', fontSize: '14px' }}>@{selectedPage.username}</p>
              </div>

              <div style={styles.adminPanel}>
                <h4 style={styles.adminPanelTitle}>🛠️ Page Management</h4>
                <div style={styles.adminButtons}>
                  <button onClick={() => { setShowManageModal(false); setShowEditModal(true); }} style={styles.adminButton}>
                    ✏️ Edit Page
                  </button>
                  <button onClick={handleLoadPageUsers} style={styles.adminButton}>
                    👥 Manage Admins & Editors
                  </button>
                  <button onClick={() => navigate(`/page/${selectedPage.id}`)} style={{...styles.adminButton, backgroundColor: '#17A2A9'}}>
                    👁️ View Page
                  </button>
                  {selectedPage.ownerId === auth.currentUser?.uid && (
                    <button onClick={handleDeletePage} style={{...styles.adminButton, backgroundColor: '#FF4444'}}>
                      🗑️ Delete Page
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit Page Modal */}
      {showEditModal && selectedPage && (
        <EditPageModal 
          page={selectedPage}
          onClose={() => setShowEditModal(false)}
          onUpdate={handleUpdatePage}
        />
      )}

      {/* Manage Users Modal */}
      {showUsersModal && selectedPage && (
        <ManageUsersModal
          page={selectedPage}
          users={managingUsers}
          loading={loadingUsers}
          onClose={() => setShowUsersModal(false)}
          onAddAdmin={handleAddAdmin}
          onRemoveAdmin={handleRemoveAdmin}
          onAddEditor={handleAddEditor}
          onRemoveEditor={handleRemoveEditor}
          isOwner={selectedPage.ownerId === (auth.currentUser?.uid || '')}
          canManageEditors={selectedPage.ownerId === (auth.currentUser?.uid || '') || selectedPage.admins.includes(auth.currentUser?.uid || '')}
        />
      )}
    </div>
  );

  async function handleDeletePage() {
    if (!selectedPage || !auth.currentUser) return;
    
    if (selectedPage.ownerId !== auth.currentUser.uid) {
      alert('Only the page owner can delete this page');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${selectedPage.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deletePage(selectedPage.id);
      alert('Page deleted successfully');
      setShowManageModal(false);
      setSelectedPage(null);
      loadPages();
    } catch (error) {
      console.error('Failed to delete page:', error);
      alert('Failed to delete page');
    }
  }

  async function handleLoadPageUsers() {
    if (!selectedPage) return;
    
    setLoadingUsers(true);
    setShowManageModal(false);
    setShowUsersModal(true);
    
    try {
  const allUserIds = Array.from(new Set([
        selectedPage.ownerId,
        ...selectedPage.admins,
        ...selectedPage.editors
  ]));
      
      const res = await listUsersByIds(allUserIds);
      const loadedUsers = res.users.map((u) => ({ id: (u as any).uid || (u as any).id, ...u }));
      setManagingUsers(loadedUsers as any[]);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoadingUsers(false);
    }
  }

  async function handleAddAdmin(userId: string) {
    if (!selectedPage) return;
    
    try {
      await updatePageTeam(selectedPage.id, { action: 'add', role: 'admin', userId });
      
      alert('Admin added successfully');
      loadPages();
    } catch (error) {
      console.error('Failed to add admin:', error);
      alert('Failed to add admin');
    }
  }

  async function handleRemoveAdmin(userId: string) {
    if (!selectedPage || userId === selectedPage.ownerId) {
      alert('Cannot remove the page owner');
      return;
    }

    try {
      await updatePageTeam(selectedPage.id, { action: 'remove', role: 'admin', userId });
      
      alert('Admin removed successfully');
      loadPages();
    } catch (error) {
      console.error('Failed to remove admin:', error);
      alert('Failed to remove admin');
    }
  }

  async function handleAddEditor(userId: string) {
    if (!selectedPage) return;
    
    try {
      await updatePageTeam(selectedPage.id, { action: 'add', role: 'editor', userId });
      
      alert('Editor added successfully');
      loadPages();
    } catch (error) {
      console.error('Failed to add editor:', error);
      alert('Failed to add editor');
    }
  }

  async function handleRemoveEditor(userId: string) {
    if (!selectedPage) return;

    try {
      await updatePageTeam(selectedPage.id, { action: 'remove', role: 'editor', userId });
      
      alert('Editor removed successfully');
      loadPages();
    } catch (error) {
      console.error('Failed to remove editor:', error);
      alert('Failed to remove editor');
    }
  }

  async function handleUpdatePage(updates: any) {
    if (!selectedPage) return;

    try {
      await updatePage(selectedPage.id, updates);
      
      alert('Page updated successfully');
      setShowEditModal(false);
      loadPages();
    } catch (error) {
      console.error('Failed to update page:', error);
      alert('Failed to update page');
    }
  }
};

const EditPageModal: React.FC<{
  page: Page;
  onClose: () => void;
  onUpdate: (updates: any) => void;
}> = ({ page, onClose, onUpdate }) => {
  const [name, setName] = useState(page.name);
  const [description, setDescription] = useState(page.description);
  const [category, setCategory] = useState(page.category);
  const [website, setWebsite] = useState(page.website || '');
  const [email, setEmail] = useState(page.email || '');
  const [phone, setPhone] = useState(page.phone || '');
  const [location, setLocation] = useState(page.location || '');
  const [updating, setUpdating] = useState(false);

  const handleUpdate = async () => {
    if (!name.trim() || !description.trim()) {
      alert('Name and description are required');
      return;
    }

    setUpdating(true);
    try {
      await onUpdate({
        name,
        description,
        category,
        website,
        email,
        phone,
        location
      });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <div style={styles.modalOverlay} onClick={onClose} />
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Edit Page</h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>
        
        <div style={styles.modalContent}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Page Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={styles.input}
              maxLength={60}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={styles.textarea}
              rows={4}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Category</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Website</label>
            <input
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Phone</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Location</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button 
              onClick={handleUpdate} 
              disabled={updating || !name.trim() || !description.trim()} 
              style={styles.submitButton}
            >
              {updating ? 'Updating...' : 'Update Page'}
            </button>
            <button onClick={onClose} style={styles.cancelButton}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const ManageUsersModal: React.FC<{
  page: Page;
  users: any[];
  loading: boolean;
  onClose: () => void;
  onAddAdmin: (userId: string) => void;
  onRemoveAdmin: (userId: string) => void;
  onAddEditor: (userId: string) => void;
  onRemoveEditor: (userId: string) => void;
  isOwner: boolean;
  canManageEditors: boolean;
}> = ({ page, users, loading, onClose, onAddAdmin, onRemoveAdmin, onAddEditor, onRemoveEditor, isOwner, canManageEditors }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      const res = await searchUsers({ q: searchQuery.trim(), limit: 20 });
      const normalized = (res.users || []).map((u: any) => ({
        id: u.uid,
        name: u.name,
        username: u.username,
        photoURL: u.photoURL,
      }));
      setSearchResults(normalized);
    } catch (error) {
      console.error('Failed to search users:', error);
    } finally {
      setSearching(false);
    }
  };

  return (
    <>
      <div style={styles.modalOverlay} onClick={onClose} />
      <div style={{...styles.modal, maxWidth: '600px'}}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Manage Admins & Editors</h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>
        
        <div style={styles.modalContent}>
          <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#F5EDE1', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: '16px', color: '#072B2C' }}>Add New Admin or Editor</h4>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name..."
                style={{...styles.input, flex: 1, margin: 0}}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button onClick={handleSearch} disabled={searching} style={styles.submitButton}>
                Search
              </button>
            </div>
            
            {searchResults.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                {searchResults.map((user) => (
                  <div key={user.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', backgroundColor: '#FFFFFF', borderRadius: '6px', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <ImageWithFallback src={user.photoURL} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} />
                      <span style={{ fontSize: '14px', color: '#072B2C' }}>{user.name}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {isOwner && (
                        <button onClick={() => onAddAdmin(user.id)} style={{...styles.memberActionButton, backgroundColor: '#FF9800', fontSize: '12px', padding: '4px 8px'}}>
                          Add Admin
                        </button>
                      )}
                      {canManageEditors && (
                        <button onClick={() => onAddEditor(user.id)} style={{...styles.memberActionButton, backgroundColor: '#4CAF50', fontSize: '12px', padding: '4px 8px'}}>
                          Add Editor
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <h4 style={{ margin: '0 0 12px', fontSize: '16px', color: '#072B2C' }}>Current Team</h4>
          {loading ? null : (
            <div style={styles.membersList}>
              {users.map((user) => {
                const isOwner = user.id === page.ownerId;
                const isAdmin = page.admins.includes(user.id);
                const isEditor = page.editors.includes(user.id);
                
                return (
                  <div key={user.id} style={styles.memberItem}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <ImageWithFallback src={user.photoURL} alt="" style={styles.memberAvatar} />
                      <div>
                        <strong style={styles.memberName}>{user.name || 'Anonymous'}</strong>
                        <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                          {isOwner && <span style={styles.roleBadge}>👑 Owner</span>}
                          {isAdmin && !isOwner && <span style={{...styles.roleBadge, backgroundColor: '#FF9800'}}>⭐ Admin</span>}
                          {isEditor && <span style={{...styles.roleBadge, backgroundColor: '#4CAF50'}}>✏️ Editor</span>}
                        </div>
                      </div>
                    </div>
                    
                    {!isOwner && (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {isOwner && isAdmin && (
                          <button 
                            onClick={() => onRemoveAdmin(user.id)}
                            style={{...styles.memberActionButton, backgroundColor: '#FF9800'}}
                          >
                            Remove Admin
                          </button>
                        )}
                        {canManageEditors && isEditor && (
                          <button 
                            onClick={() => onRemoveEditor(user.id)}
                            style={{...styles.memberActionButton, backgroundColor: '#4CAF50'}}
                          >
                            Remove Editor
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const styles: { [key: string]: any } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--color-background-alt)',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '50vh',
    fontSize: '18px',
    color: 'var(--color-text)',
  },
  main: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '76px 20px 90px',
  },
  mainMobile: {
    padding: '76px 12px 90px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: 'var(--color-text)',
    margin: 0,
  },
  createButton: {
    padding: '12px 24px',
    backgroundColor: '#0B6B6F',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'transform 0.2s',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: 'var(--color-surface)',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  emptyButton: {
    marginTop: '20px',
    padding: '14px 28px',
    backgroundColor: '#17A2A9',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
  },
  pagesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '24px',
  },
  pageCard: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  cardCover: {
    height: '150px',
    overflow: 'hidden',
  },
  coverImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  cardContent: {
    padding: '20px',
    textAlign: 'center',
    position: 'relative',
  },
  pageAvatar: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '4px solid #FFFFFF',
    marginTop: '-50px',
    marginBottom: '10px',
  },
  pageName: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: 'var(--color-text)',
    margin: '0 0 5px 0',
  },
  pageUsername: {
    fontSize: '14px',
    color: 'var(--color-text-muted)',
    margin: '0 0 10px 0',
  },
  pageType: {
    display: 'inline-block',
    padding: '4px 12px',
    backgroundColor: 'var(--color-primary-300)',
    color: 'var(--color-primary)',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
    marginBottom: '10px',
  },
  pageFollowers: {
    fontSize: '14px',
    color: 'var(--color-text-muted)',
    margin: 0,
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 999,
  },
  modal: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    backgroundColor: 'var(--color-surface)',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
    zIndex: 1000,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid var(--color-border)',
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: 'var(--color-text)',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    cursor: 'pointer',
    color: 'var(--color-text-muted)',
    padding: 0,
    lineHeight: 1,
  },
  modalContent: {
    padding: '24px',
    overflowY: 'auto',
  },
  formGroup: {
    marginBottom: '20px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--color-text)',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '10px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '2px solid var(--color-primary-600)',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '2px solid var(--color-primary-600)',
    backgroundColor: 'var(--color-surface)',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '2px solid var(--color-primary-600)',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  fileInput: {
    width: '100%',
    padding: '8px',
    fontSize: '14px',
  },
  hint: {
    fontSize: '12px',
    color: 'var(--color-text-muted)',
    marginTop: '4px',
    display: 'block',
  },
  progressBar: {
    width: '100%',
    height: '6px',
    backgroundColor: '#E0E0E0',
    borderRadius: '3px',
    overflow: 'hidden',
    marginBottom: '20px',
  },
  progressFill: {
    height: '100%',
    backgroundColor: 'var(--color-primary-600)',
    transition: 'width 0.3s',
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  submitButton: {
    padding: '12px 24px',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-surface)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'transform 0.2s',
  },
  cancelButton: {
    padding: '12px 24px',
    backgroundColor: 'var(--color-border)',
    color: 'var(--color-text)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'transform 0.2s',
  },
  adminPanel: {
    marginTop: '20px',
    padding: '20px',
    backgroundColor: '#FFF3E0',
    borderRadius: '12px',
    border: '2px solid var(--color-accent)',
  },
  adminPanelTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#E65100',
    marginBottom: '15px',
  },
  adminButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
  },
  adminButton: {
    padding: '10px 16px',
    backgroundColor: 'var(--color-accent)',
    color: 'var(--color-surface)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  membersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  memberItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: 'var(--color-background-alt)',
    borderRadius: '8px',
  },
  memberAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  memberName: {
    fontSize: '15px',
    color: 'var(--color-text)',
  },
  roleBadge: {
    display: 'inline-block',
    padding: '3px 8px',
    backgroundColor: 'var(--color-accent)',
    color: 'var(--color-surface)',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
  },
  memberActionButton: {
    padding: '6px 12px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    color: 'var(--color-surface)',
  },
};

export default BusinessPages;
